/**
 * Client-side logo preparation — turning the file an owner picks into artwork the page
 * can show, store and download, plus the checks that tell them whether it will actually
 * work where the logo is used.
 *
 * Every logo slot has a canonical size (see `SLOT_SPECS` in `./logos`): the branding
 * pipeline's own output dimensions. A replacement is rendered in the same places (site
 * chrome, onboarding pages, transactional emails, the favicon), so it has to keep that
 * slot's aspect ratio and carry enough resolution. This module measures the picked file
 * against that baseline and encodes it as a `data:` URI — the form the art store's upload
 * route accepts (see `./logo-api`).
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: clean the artwork up. Background removal and the
 * refit into each slot's lockup belong to the branding pipeline, which does them properly
 * and which the owner opts into per replacement (`prepareUpload` on the run). Approximating
 * either here would mean shipping the browser's guess to the server and asking it to work
 * from that, so the file travels EXACTLY as the owner picked it and every decision about it
 * is made in one place.
 *
 * SVG is passed through untouched: it is resolution-independent, so rasterising it would
 * only lose quality (the upload route rasterises it server-side).
 */

export type PixelSize = { width: number; height: number };

/** Owner-picked file bounds. 10 MB is the ceiling the backend itself enforces. */
export const MAX_LOGO_FILE_BYTES = 10 * 1024 * 1024;
export const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

/**
 * Byte ceiling for the artwork sent to the art store. The file itself may be up to
 * {@link MAX_LOGO_FILE_BYTES}, but it travels base64-encoded inside a JSON body (~4 bytes
 * per 3 bytes of image), so a very large picture is re-encoded smaller rather than pushed
 * through as a multi-megabyte request. Generous enough that a real logo never loses
 * detail: the pipeline's own output is a fraction of this.
 */
export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

/** Upper bound on the longest side when a slot has no canonical size to aim at. */
const FALLBACK_MAX_SIDE = 1536;

/**
 * How far a picked image may drift from the slot's canonical aspect before we warn.
 *
 * Deliberately generous: the canonical sizes are a TIDIED target (see `SLOT_SPECS`), so
 * artwork the branding pipeline itself produced sits ~10% off some of them. Warning at
 * that distance would nag about files that are perfectly usable; at 15% only a genuinely
 * different shape (a square mark for a 6:1 wordmark slot) speaks up.
 */
export const RATIO_TOLERANCE = 0.15;

export const isAllowedLogoFile = (file: File): boolean =>
  file.size <= MAX_LOGO_FILE_BYTES && ALLOWED_LOGO_TYPES.includes(file.type);

export const isSvg = (file: File): boolean => file.type === 'image/svg+xml';

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

/** Tidy aspect-ratio caption ("16:5", or "3.2:1" when it will not reduce cleanly). */
export const formatRatio = (width: number, height: number): string => {
  if (!width || !height) return '';
  const divisor = gcd(width, height) || 1;
  const rw = Math.round(width / divisor);
  const rh = Math.round(height / divisor);
  return rw <= 32 && rh <= 32 ? `${rw}:${rh}` : `${(width / height).toFixed(1)}:1`;
};

/** The largest box with `size`'s aspect that fits inside `max`. Never upscales. */
export const fitWithin = (size: PixelSize, max: PixelSize): PixelSize => {
  if (size.width <= 0 || size.height <= 0) return size;
  const scale = Math.min(max.width / size.width, max.height / size.height, 1);
  return { width: Math.max(1, Math.round(size.width * scale)), height: Math.max(1, Math.round(size.height * scale)) };
};

/** Relative difference between two aspect ratios (0 = identical). */
export const aspectDrift = (size: PixelSize, target: PixelSize): number => {
  if (!size.width || !size.height || !target.width || !target.height) return 0;
  const ratio = size.width / size.height;
  const wanted = target.width / target.height;
  return Math.abs(ratio - wanted) / wanted;
};

/**
 * How much smaller than the canonical size a picked logo may be before we warn. Slightly
 * under is harmless (the asset is scaled to fit wherever it's used); well under goes soft
 * on high-density screens, which is what this catches.
 */
export const MIN_RESOLUTION_FACTOR = 0.75;

/** The picked artwork carries meaningfully less resolution than the slot's baseline. */
export const isBelowResolution = (source: PixelSize, canonical: PixelSize): boolean =>
  source.width > 0 &&
  source.height > 0 &&
  (source.width < canonical.width * MIN_RESOLUTION_FACTOR || source.height < canonical.height * MIN_RESOLUTION_FACTOR);

/** The picked artwork is proportioned differently from the slot's baseline. */
export const isOffRatio = (source: PixelSize, canonical: PixelSize): boolean =>
  source.width > 0 && source.height > 0 && aspectDrift(source, canonical) > RATIO_TOLERANCE;

/** Decoded byte size of a `data:` URI (base64 or percent-encoded). */
export const dataUriBytes = (uri: string): number => {
  const comma = uri.indexOf(',');
  if (comma === -1) return 0;
  const body = uri.slice(comma + 1);
  if (!uri.slice(0, comma).includes(';base64')) return body.length;
  const padding = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((body.length * 3) / 4) - padding);
};

// --- Browser steps -----------------------------------------------------------

/** Read a picked file as a `data:` URI. */
export const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const { result } = reader;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('unreadable file'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('unreadable file'));
    reader.readAsDataURL(file);
  });

/** Decode a `data:` URI into an image element (so its natural size can be read). */
export const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('undecodable image'));
    image.src = src;
  });

/** Draw a source (image or canvas) into a fresh canvas of exactly `size`. */
const drawTo = (source: CanvasImageSource, size: PixelSize): HTMLCanvasElement | null => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, size.width);
  canvas.height = Math.max(1, size.height);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
};

/** Encode a canvas, re-drawing it progressively smaller until it fits `budget` bytes. */
const encodeWithin = (canvas: HTMLCanvasElement, mime: string, budget: number): { src: string; size: PixelSize } => {
  let current = canvas;
  let quality = 0.92;
  let src = current.toDataURL(mime, quality);
  // Logos are flat artwork, so this converges in a step or two; the cap stops a
  // pathological photo from looping.
  for (let attempt = 0; attempt < 4 && dataUriBytes(src) > budget; attempt += 1) {
    const next = drawTo(current, {
      width: Math.max(1, Math.round(current.width * 0.75)),
      height: Math.max(1, Math.round(current.height * 0.75)),
    });
    if (!next) break;
    current = next;
    quality = Math.max(0.6, quality - 0.08);
    src = current.toDataURL(mime, quality);
  }
  return { src, size: { width: current.width, height: current.height } };
};

// --- Preparing a picked file -------------------------------------------------

/** A prepared replacement, ready to be stored and rendered. */
export type PreparedLogo = {
  /** The prepared artwork as a `data:` URI. */
  src: string;
  /** Pixel size of the artwork (0×0 for an SVG whose intrinsic size can't be read). */
  size: PixelSize;
  /** Pixel size of the file the owner picked, before any preparation. */
  source: PixelSize;
  /** True for SVG — passed through as-is, so no size checks apply. */
  vector: boolean;
};

const passthrough = (src: string, source: PixelSize, vector: boolean): PreparedLogo => ({
  src,
  size: source,
  source,
  vector,
});

/**
 * Read a picked file into the artwork that will be uploaded: measure it, and re-encode it
 * only if it is too large to send comfortably.
 *
 * The artwork itself is NOT altered — the file goes to the pipeline exactly as the owner
 * picked it, and what happens to it there is their `prepareUpload` answer, not ours.
 *
 * Returns both the encoded artwork and the ORIGINAL size, which is what the resolution /
 * aspect-ratio checks are made against — the caller warns on those, it does not block,
 * since an owner may knowingly supply a differently-proportioned mark.
 */
export const prepareLogo = async (file: File): Promise<PreparedLogo> => {
  const dataUrl = await readFileAsDataUrl(file);
  if (isSvg(file)) return passthrough(dataUrl, { width: 0, height: 0 }, true);

  const image = await loadImage(dataUrl);
  const source: PixelSize = { width: image.naturalWidth, height: image.naturalHeight };

  // Small enough to send as it stands: keep the owner's bytes exactly.
  if (dataUriBytes(dataUrl) <= MAX_UPLOAD_BYTES) return passthrough(dataUrl, source, false);

  const canvas = drawTo(image, fitWithin(source, { width: FALLBACK_MAX_SIDE, height: FALLBACK_MAX_SIDE }));
  // Canvas unavailable (a headless / permission edge): send the untouched file and let the
  // upload route decide — better than failing a replacement the server may well accept.
  if (!canvas) return passthrough(dataUrl, source, false);

  const jpeg = file.type === 'image/jpeg' || file.type === 'image/jpg';
  const { src, size } = encodeWithin(canvas, jpeg ? 'image/jpeg' : 'image/png', MAX_UPLOAD_BYTES);
  return { src, size, source, vector: false };
};
